/**
 * The depth model behind the parallax. Everything in the world sits at a depth z: the playable
 * lanes at 0, 1 and 2, scenery behind and in front of them. The camera sits at the depth of the
 * player's lane (camZ), and a layer at depth z is drawn scaled by depthScale(z, camZ) around the
 * centre of the screen. Farther layers come out smaller and move slower: the parallax is a
 * consequence of the projection, not a per-layer number to tune.
 */

/** How quickly things shrink with distance. 0.22 → lanes 1 and 2 at 82% and 69%. */
export const DEPTH_FALLOFF = 0.22;

/** Depths of the scenery layers (lanes are 0, 1, 2). */
export const DEPTH = {
  foreground: -0.8,
  mid: 5,
  far: 14,
} as const;

/** Screen-space scale (and scroll factor) of something at depth z seen from camZ. */
export function depthScale(z: number, camZ: number): number {
  return 1 / Math.max(0.2, 1 + DEPTH_FALLOFF * (z - camZ));
}

/**
 * Opacity of a lane: lanes nearer than the camera fade so the player can be seen through them
 * (as in Klonoa); the player's own lane and those behind it are opaque.
 */
export function laneAlpha(z: number, camZ: number): number {
  const inFront = camZ - z;
  if (inFront <= 0) return 1;
  return Math.max(0.3, 1 - inFront * 0.9);
}

/** Moves value toward target by a fraction that is the same per second at any frame rate. */
export function ease(
  value: number,
  target: number,
  ratePerSecond: number,
  dtSeconds: number,
): number {
  return target + (value - target) * Math.exp(-ratePerSecond * dtSeconds);
}
